import { SuccessPage } from "@/components/pages/SuccessPage";
import { useDiaryByIdQuery } from "@/features/diaries/api";
import { hasActionableDueEntry } from "@/features/diaries/utils/sequentialFormHelpers";
import { getFormEntrySuccessLoadingSteps, useEntryCompleteCopy } from "@/features/i18n/entryCompleteCopy";
import { useAppLocale } from "@/features/i18n/LocaleProvider";
import { usePatientsCopy } from "@/features/i18n/patientsCopy";
import { createFileRoute } from "@tanstack/react-router";
import { CheckCircle2 } from "lucide-react";
import { useMemo } from "react";

const EntryComplete = () => {
    const copy = usePatientsCopy();
    const entryCompleteCopy = useEntryCompleteCopy();
    const { locale } = useAppLocale();
    const loadingSteps = useMemo(() => getFormEntrySuccessLoadingSteps(locale), [locale]);
    const { diaryId } = Route.useParams();

    const { data: diary, isFetching } = useDiaryByIdQuery(diaryId as string);

    // Count remaining due trackers for clinician
    const remainingActionableTrackers =
        diary?.trackers_view?.filter((t) => {
            const hasClinicianRole = t.roles?.includes("clinician");
            return hasClinicianRole && hasActionableDueEntry(t);
        }).length ?? 0;

    const totalRemaining = remainingActionableTrackers;
    const isDue = totalRemaining > 0;

    return (
        <SuccessPage
            title={copy.entryRecordedTitle}
            description={isDue ? copy.entryRecordedPending(totalRemaining) : copy.entryRecordedComplete}
            icon={CheckCircle2}
            isLoading={isFetching}
            loadingText={entryCompleteCopy.loading}
            loadingDescription={entryCompleteCopy.justAMoment}
            loadingSteps={loadingSteps}
            primaryButton={{
                text: isDue ? entryCompleteCopy.continueToNextEntry : copy.returnToPatient,
                to: "/patients/$diaryId",
                params: { diaryId },
            }}
        />
    );
};

export const Route = createFileRoute("/_app/diaries/$diaryId/tracker/$trackerId/entry/$entryId/complete")({
    component: EntryComplete,
});
